import React from 'react';
import { ENCAISSEMENT } from '@/constants';

/**
 * Hook de rafraîchissement automatique d'un écran.
 *
 * Dès que `actif` est vrai, appelle `refresh` une première fois (entrée sur l'écran), puis à
 * intervalle régulier tant que l'onglet est visible, et dès que l'onglet redevient visible. Un
 * onglet masqué n'émet aucune requête.
 *
 * C'est toujours la dernière version de `refresh` qui est appelée : un changement de référence ne
 * relance pas le cycle.
 */
const useAutoRefresh = (
  refresh: () => void,
  actif = true,
  intervalle: number = ENCAISSEMENT.INTERVALLE_RAFRAICHISSEMENT_INDICATEURS,
): void => {
  const refreshRef = React.useRef(refresh);

  React.useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  React.useEffect(() => {
    if (!actif) return undefined;

    const rafraichir = () => refreshRef.current();
    const rafraichirSiVisible = () => {
      if (document.visibilityState === 'visible') {
        rafraichir();
      }
    };

    // ⚠️ Premier appel différé, et non immédiat : React exécute les effets des enfants avant
    // ceux des parents, et les intercepteurs X-Regie et X-Regisseur des providers parents
    // pourraient ne pas encore être posés (cf. useInterrogationPaiementsMFO).
    const premierAppel = setTimeout(rafraichir, 0);
    const intervalleId = setInterval(rafraichirSiVisible, intervalle);
    document.addEventListener('visibilitychange', rafraichirSiVisible);

    return () => {
      clearTimeout(premierAppel);
      clearInterval(intervalleId);
      document.removeEventListener('visibilitychange', rafraichirSiVisible);
    };
  }, [actif, intervalle]);
};

export default useAutoRefresh;
